// client/src/components/services_tester/ServicesTesterContent.jsx
import React, { useCallback } from 'react';
import { Box, Card, CardContent, Tabs, Tab } from '@mui/material';
import {
  Api,
  Analytics,
  Terminal,
  Assessment,
  Image as ImageIcon,
} from '@mui/icons-material';
import ServicesPanel from './ServicesPanel';
import InfrastructurePanel from './InfrastructurePanel';
import RestApiTab from './RestApiTab';
import MonitoringTab from './MonitoringTab';
import ImageLangPanel from './ImageLangPanel';
import LogsPanel from '../LogsPanel';
import DeepCodeAnalyzer from '../DeepCodeAnalyzer';
import { testImageLangCodec } from '../../store/thunks/directServicesThunks';

// Индексы табов (совпадают с порядком <Tab>)
const TAB = {
  REST: 0,
  MONITORING: 1,
  LOGS: 2,
  AI: 3,
  IMAGE_LANG: 4,
};

export default function ServicesTesterContent({
  activeTab,
  isAuthenticated,
  user,
  networkDiagnostics,
  healthCheckLoading,
  rabbitmqStatus,
  tarantoolStatus,
  mongodbStatus,
  clickhouseStatus,
  featureTreeStatus,
  kratosStatus,
  selectedService,
  restPath,
  restMethod,
  restBody,
  restResponse,
  restLoading,
  infrastructureLoading,
  logs,
  autoScroll,
  logFilter,
  logExpanded,
  logSearch,
  logLevels,
  logAreaRef,
  handleServiceTest,
  handleSendRestRequest,
  handleSendCustomRequest,
  handleClearLogs,
  handleExportLogs,
  scrollToTop,
  scrollToBottom,
  handleRabbitMQHealth,
  handleRabbitMQDetails,
  handleRabbitMQTest,
  handleKratosHealth,
  handleKratosAdminHealth,
  handleKratosUsers,
  handleKratosDetails,
  handleKratosDetailed,
  handleFeatureTreeCheck,
  handleTarantoolHealth,
  handleMongoDBHealth,
  handleClickHouseHealth,
  handleClickHouseDetailed,
  handleTarantoolDetailed,
  handleMongoDBDetailed,
  handleCheckAuth,
  setActiveTab,
  setSelectedService,
  setRestPath,
  setRestMethod,
  setRestBody,
  setAutoScroll,
  setLogFilter,
  setLogExpanded,
  setLogSearch,
  setLogLevels,
  isSendRequestDisabled,
  selectedServiceRequiresAuth,
  isValidJson,
  imageLangEncodeResult,
  imageLangDecodeResult,
  handleImageLangEncode,
  handleImageLangDecode,
  dispatch,
}) {
  // ============================================================================
  // Props для левых панелей
  // ============================================================================
  const servicesPanelProps = {
    networkDiagnostics,
    healthCheckLoading,
    isAuthenticated,
    handleServiceTest,
  };

  const infrastructurePanelProps = {
    rabbitmqStatus,
    tarantoolStatus,
    mongodbStatus,
    clickhouseStatus,
    kratosStatus,
    infrastructureLoading,
    handleRabbitMQHealth,
    handleRabbitMQDetails,
    handleRabbitMQTest,
    handleKratosHealth,
    handleKratosAdminHealth,
    handleKratosUsers,
    handleKratosDetails,
    handleKratosDetailed,
    handleTarantoolHealth,
    handleMongoDBHealth,
    handleClickHouseHealth,
    handleClickHouseDetailed,
    handleTarantoolDetailed,
    handleMongoDBDetailed,
  };

  // ============================================================================
  // Props для правой панели
  // ============================================================================
  const restApiTabProps = {
    isAuthenticated,
    selectedService,
    restPath,
    restMethod,
    restBody,
    restResponse,
    restLoading,
    setSelectedService,
    setRestPath,
    setRestMethod,
    setRestBody,
    handleSendRestRequest,
    isSendRequestDisabled,
    selectedServiceRequiresAuth,
    isValidJson,
  };

  const handleTestImageLangCodec = useCallback(() => {
    if (dispatch) dispatch(testImageLangCodec());
  }, [dispatch]);

  const monitoringTabProps = {
    networkDiagnostics,
    isAuthenticated,
    rabbitmqStatus,
    tarantoolStatus,
    mongodbStatus,
    clickhouseStatus,
    kratosStatus,
    infrastructureLoading,
    handleCheckAuth,
    handleRabbitMQHealth,
    handleRabbitMQDetails,
    handleRabbitMQTest,
    handleKratosHealth,
    handleKratosAdminHealth,
    handleKratosUsers,
    handleKratosDetails,
    handleKratosDetailed,
    handleTarantoolHealth,
    handleMongoDBHealth,
    handleClickHouseHealth,
    handleClickHouseDetailed,
    handleTarantoolDetailed,
    handleMongoDBDetailed,
    handleTestImageLangCodec,
  };

  const logsPanelProps = {
    logs,
    autoScroll,
    setAutoScroll,
    logFilter,
    setLogFilter,
    logExpanded,
    setLogExpanded,
    logSearch,
    setLogSearch,
    logLevels,
    setLogLevels,
    clearLogs: handleClearLogs,
    exportLogs: handleExportLogs,
    scrollToTop,
    scrollToBottom,
    logAreaRef,
  };

  const imageLangPanelProps = {
    onEncode: handleImageLangEncode,
    onDecode: handleImageLangDecode,
    encodeResult: imageLangEncodeResult,
    decodeResult: imageLangDecodeResult,
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        gap: 0.75,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* ========== LEFT PANEL — Микросервисы + Инфраструктура ========== */}
      <Box
        sx={{
          width: 280,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          minHeight: 0,
          overflow: 'auto',
          flexShrink: 0,
        }}
      >
        <ServicesPanel {...servicesPanelProps} />
        <InfrastructurePanel {...infrastructurePanelProps} />
      </Box>

      {/* ========== RIGHT PANEL — Tabs ========== */}
      <Card
        variant="outlined"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
          borderRadius: 1,
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '&:last-child': { pb: 0 },
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 1,
              minHeight: 30,
              flexShrink: 0,
              '& .MuiTab-root': {
                minHeight: 30,
                py: 0.25,
                fontSize: '0.65rem',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<Api fontSize="small" />} iconPosition="start" label="REST API" />
            <Tab icon={<Analytics fontSize="small" />} iconPosition="start" label="Мониторинг" />
            <Tab icon={<Terminal fontSize="small" />} iconPosition="start" label="Логи" />
            <Tab icon={<Assessment fontSize="small" />} iconPosition="start" label="AI" />
            <Tab icon={<ImageIcon fontSize="small" />} iconPosition="start" label="ImageLang" />
          </Tabs>

          {/* Контент вкладок */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: activeTab === TAB.AI ? 0 : 0.5,
              minHeight: 0,
            }}
          >
            {activeTab === TAB.REST && <RestApiTab {...restApiTabProps} />}
            {activeTab === TAB.MONITORING && <MonitoringTab {...monitoringTabProps} />}
            {activeTab === TAB.LOGS && <LogsPanel {...logsPanelProps} />}
            {activeTab === TAB.AI && <DeepCodeAnalyzer />}
            {activeTab === TAB.IMAGE_LANG && <ImageLangPanel {...imageLangPanelProps} />}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
